import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { getSongById, DEMO_SONGS } from "@/data/demoSongs";
import { useApp } from "@/context/AppContext";
import { useJamEngine } from "@/hooks/useJamEngine";
import { SessionSummary } from "@/models/types";
import { Fretboard } from "@/components/jam/Fretboard";
import { BeatPulse } from "@/components/jam/BeatPulse";
import { GuidanceMessage } from "@/components/jam/GuidanceMessage";
import { TransitionCue } from "@/components/jam/TransitionCue";
import { RecoveryOverlay } from "@/components/jam/RecoveryOverlay";
import { JamControls } from "@/components/jam/JamControls";
import { JamTimeline } from "@/components/jam/JamTimeline";
import { ModeSelector } from "@/components/jam/ModeSelector";
import { DevPanel } from "@/components/jam/DevPanel";
import { playFretNote } from "@/audio/synth";
import { useI18n } from "@/i18n/I18nContext";

export default function Jam() {
  const { songId } = useParams();
  const navigate = useNavigate();
  const song = getSongById(songId || "") ?? DEMO_SONGS[0];
  const { mode, setMode, resumeTake, setResumeTake, setPendingSession } = useApp();
  const { tm } = useI18n();

  const startTime = resumeTake && resumeTake.songId === songId ? resumeTake.lastTime : 0;
  const variation = resumeTake?.variation ?? 0;
  const [rate, setRate] = useState(1);
  const [ended, setEnded] = useState(false);

  const engine = useJamEngine({
    song: song!,
    mode,
    startTime,
    onEnded: () => finish(true),
  });
  const js = engine.jamState;

  // apply tempo (audio position -> guidance mapping stays intact)
  useEffect(() => {
    if (engine.audioRef.current) engine.audioRef.current.playbackRate = rate;
  }, [rate, engine.audioRef]);

  // recompute when mode changes while paused
  useEffect(() => {
    if (!engine.isPlaying) engine.seek(js.currentTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // clear a consumed resume take once mounted
  useEffect(() => {
    return () => setResumeTake(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildSummary = (): SessionSummary => {
    const r = engine.recoveries;
    return {
      songId: song!.id,
      songTitle: song!.title,
      mode,
      flow: r === 0 ? "DEEP" : r <= 1 ? "GOOD" : "STEADY",
      transitionsHit: engine.transitionsHit,
      transitionsTotal: engine.transitionsTotal,
      recoveries: r,
      comfortZone: js.activeZone?.label ?? "Position 1",
      durationPlayed: js.currentTime,
    };
  };

  const finish = (fromEnd = false) => {
    if (ended) return;
    setEnded(true);
    engine.pause();
    setPendingSession({ summary: buildSummary(), variation });
    navigate("/complete");
  };

  const beatsPerBar = song ? song.timeSignature[0] : 4;
  const beatIntensity = useMemo(() => (mode === "FREE" ? 0.4 : mode === "FLOW" ? 0.7 : 1), [mode]);

  if (!song) {
    return null;
  }

  const recovering = js.recoveryLevel > 0;

  return (
    <div
      data-testid="jam-screen"
      className="relative flex h-[100dvh] flex-col overflow-hidden bg-[#0A0C0E]"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <audio ref={engine.audioRef} src={song.audioUrl} preload="auto" />

      {/* Top bar */}
      <div className="flex items-center justify-between gap-2 px-4 pt-3">
        <button
          data-testid="jam-back-btn"
          onClick={() => finish(true)}
          className="flex items-center gap-1 text-slate-400 transition hover:text-slate-200"
        >
          <ChevronLeft size={20} />
        </button>

        <div className="flex flex-col items-center leading-tight">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-400/90">
            {tm("sections", js.currentSection)}
          </span>
          <span className="text-sm font-medium text-slate-200">{song.title}</span>
        </div>

        <div className="flex items-center gap-2">
          <span
            data-testid="jam-current-chord"
            className="rounded-md px-2 py-1 font-mono text-xs text-amber-200"
            style={{ background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.25)" }}
          >
            {js.currentChord}
          </span>
        </div>
      </div>

      {/* Mode + transition cue */}
      <div className="mt-3 flex items-center justify-between px-4">
        <ModeSelector mode={mode} onChange={setMode} compact />
        <TransitionCue
          phase={js.transitionPhase}
          secondsUntil={js.secondsUntilNextTransition}
          nextZone={js.nextZone}
          intensity={js.transitionIntensity}
        />
      </div>

      {/* Guidance message */}
      <GuidanceMessage message={js.guidanceMessage} recovery={recovering} />

      {/* Fretboard — the centerpiece */}
      <div className="relative z-30 min-h-0 flex-1 px-3 sm:px-6">
        <Fretboard
          zone={js.activeZone}
          litNotes={js.activeNotes}
          ghostNotes={js.ghostNotes}
          recoveryLevel={js.recoveryLevel}
          transitionIntensity={js.transitionIntensity}
          mode={js.guidanceMode}
          onNoteTap={(n) => playFretNote(n.string, n.fret)}
        />
      </div>

      {/* Beat pulse */}
      <div className="py-3">
        <BeatPulse
          currentBeat={js.currentBeat}
          beatsPerBar={beatsPerBar}
          bpm={song.bpm}
          intensity={beatIntensity}
        />
      </div>

      {/* Bottom deck */}
      <div className="relative z-30 space-y-4 px-4 pb-4">
        <JamTimeline
          song={song}
          currentTime={js.currentTime}
          duration={js.duration}
          onSeek={engine.seek}
        />
        <JamControls
          isPlaying={engine.isPlaying}
          onToggle={engine.toggle}
          onLost={engine.triggerRecovery}
          onEnd={() => finish(true)}
          playbackRate={rate}
          onRate={setRate}
          effectiveBpm={song.bpm * rate}
        />
      </div>

      <RecoveryOverlay level={js.recoveryLevel} />

      <DevPanel
        onSimulateChord={engine.jumpToNextSection}
        onSimulateTransition={engine.jumpToNextTransition}
        onRecovery={(lvl) => engine.forceRecoveryDepth(lvl)}
        onStableFlow={() => engine.forceRecoveryDepth(null)}
      />
    </div>
  );
}
